
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SessionContext, useSessionControl } from "../authentication/SessionProvider";
import { useAppFwContext } from "../AppFwContextContextProvider";
import { _initializeInstallationId } from "../common";
import { getServiceWorkerRegistration, isServiceWorkerSupported } from "../sw/service-worker-utils";

export interface NotificationsControl {
    pushEnabled: boolean | undefined;
    isSupported: boolean;
    isInitializing: boolean;
    permission: NotificationPermission;
    subscribe: () => Promise<void>;
    unsubscribe: (disable: boolean) => Promise<void>;
}

export const NotificationsContext = createContext<NotificationsControl | undefined>(undefined);

export function useNotificationsContext() {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error("useNotificationsContext must be used within a NotificationsProvider");
    }
    return context;
}

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
    const appFwContext = useAppFwContext();
    const session = useContext(SessionContext);
    const sessionControl = useSessionControl();

    const [pushEnabled, setPushEnabled] = useState<boolean | undefined>(undefined);
    const [isInitializing, setIsInitializing] = useState(true);
    const [permission, setPermission] = useState<NotificationPermission>(
        ('Notification' in window) ? Notification.permission : 'default'
    );

    const isSupported = isServiceWorkerSupported();

    const updateWebPushSubscription = useCallback(async () => {
        const registration = await getServiceWorkerRegistration();
        if (!registration) return;

        const subscription = await registration.pushManager.getSubscription();

        await appFwContext.appFwApiClient.updateWebPushSubscription({
            installationId: _initializeInstallationId(),
            subscription: subscription,
        });
    }, [appFwContext]);

    const subscribe = useCallback(async () => {
        const notificationsConfig = appFwContext.configuration.notifications;
        if (!notificationsConfig) {
            console.error("[notifications] notificationsConfig not provided, cannot subscribe");
            return;
        }

        const registration = await getServiceWorkerRegistration();
        if (!registration) return;

        let subscription = await registration.pushManager.getSubscription();

        let granted = Notification.permission === 'granted';
        if (!granted) {
            const permissionResult = await Notification.requestPermission();
            granted = permissionResult === 'granted';
            setPermission(permissionResult);
        }

        if (granted) {
            if (subscription === null) {
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: notificationsConfig.vapidPublicKey,
                });
                console.log('[notifications] subscribed:', subscription);
            }
            await updateWebPushSubscription();

            await appFwContext.appFwApiClient.updateNotificationsConfig({ enabled: true });
            setPushEnabled(true);
        } else {
            console.log('[notifications] permission denied/dismissed, cannot subscribe');
        }
    }, [appFwContext, updateWebPushSubscription]);

    const unsubscribe = useCallback(async (disable: boolean) => {
        console.log("[notifications] start unsubscribe()");

        const registration = await getServiceWorkerRegistration();
        if (registration) {
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                await subscription.unsubscribe();
            }
        }

        await updateWebPushSubscription();

        if (disable) {
            await appFwContext.appFwApiClient.updateNotificationsConfig({ enabled: false });
            setPushEnabled(false);
        }
        console.log("[notifications] finished unsubscribe()");
    }, [appFwContext, updateWebPushSubscription]);

    // Register logout listener
    useEffect(() => {
        return sessionControl.addLogoutListener(async () => {
            console.log("[notifications] logout listener triggered");
            await unsubscribe(false);
        });
    }, [sessionControl, unsubscribe]);

    // Initialize Logic
    useEffect(() => {
        if (!appFwContext.configuration.notifications) {
            setIsInitializing(false);
            return;
        }

        if (session === undefined) {
            return;
        }

        if (session === null) {
            setPushEnabled(false);
            setIsInitializing(false);
            return;
        }

        let cancelled = false;

        const initialize = async () => {
            console.log("[notifications] initializeNotifications");

            try {
                const config = await appFwContext.appFwApiClient.getNotificationsConfig();
                console.log("[notifications] config: ", config);
                // We don't set pushEnabled yet, we check client state first.

                const registration = await getServiceWorkerRegistration();
                if (!registration) {
                    console.log("[notifications] no service worker available, cannot initialize push");
                    if (!cancelled) {
                        setPushEnabled(false);
                        setIsInitializing(false);
                    }
                    return;
                }
                console.log("[notifications] registration: ", registration);

                const subscription = await registration.pushManager.getSubscription();
                console.log("[notifications] subscription: ", subscription);

                if (config.enabled) {
                    // Server thinks we are enabled.
                    if (subscription == null) {
                        // Browser is not subscribed.
                        if (Notification.permission === 'granted') {
                            console.log("[notifications] notifications enabled, but no subscription, permission is granted ==> re-subscribe");
                            // We can reuse the subscribe logic but we need to be careful about infinite loops if we put it in dep array.
                            // For now, duplicate the core logic or call the function if it was stable (which it is via deps).
                            // Calling subscribe() implies updating config to enabled again, which is redundant but safe.
                            // However, subscribe() relies on current state maybe? No, it relies on deps.
                            // Ideally we extract internal _subscribe function.
                            // For simplicity/safetly here:
                            const notificationsConfig = appFwContext.configuration.notifications;
                            if (notificationsConfig) {
                                await registration.pushManager.subscribe({
                                    userVisibleOnly: true,
                                    applicationServerKey: notificationsConfig.vapidPublicKey,
                                });
                                await updateWebPushSubscription();
                            }
                        } else {
                            console.log("[notifications] notifications enabled, but no subscription, permission is NOT granted ==> ignore");
                        }
                    } else {
                        // Already subscribed, ensure server is in sync with latest endpoint info
                        await updateWebPushSubscription();
                    }
                    if (!cancelled) {
                        const effectiveEnabled = subscription !== null || (Notification.permission === 'granted');
                        console.log(`[notifications] initialization finished: ${effectiveEnabled ? 'enabled' : 'disabled (permission/sub missing)'}`);
                        setPushEnabled(effectiveEnabled);
                    }
                } else {
                    // Server thinks we are disabled.
                    if (subscription) {
                        console.log("[notifications] notifications disabled, but existing subscription ==> unsubscribe");
                        await subscription.unsubscribe();
                        await updateWebPushSubscription();
                    }
                    if (!cancelled) {
                        console.log("[notifications] initialization finished: disabled");
                        setPushEnabled(false);
                    }
                }
            } catch (error) {
                console.error("[notifications] initialization error", error);
            } finally {
                if (!cancelled) setIsInitializing(false);
            }
        };

        setIsInitializing(true);
        initialize();

        return () => { cancelled = true; };
    }, [appFwContext, session, updateWebPushSubscription]); // Removed 'subscribe' from deps to avoid cycle if we used it.

    // Permission change listener
    useEffect(() => {
        if ('permissions' in navigator) {
            navigator.permissions.query({ name: 'notifications' as PermissionName }).then((status) => {
                status.onchange = () => {
                    console.log('[notifications] permission changed to:', status.state);
                    // status.state is 'granted' | 'denied' | 'prompt'
                    // Notification.permission is 'granted' | 'denied' | 'default'
                    // Mappings: granted->granted, denied->denied, prompt->default
                    setPermission(Notification.permission);
                };
            });
        }
    }, []);

    const value = useMemo(() => ({
        pushEnabled,
        isSupported,
        isInitializing,
        permission,
        subscribe,
        unsubscribe
    }), [pushEnabled, isSupported, isInitializing, permission, subscribe, unsubscribe]);

    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
}
